/**
 * MyCopro - Chat Store complet avec Firestore
 * Inclut sanitization des messages et filtrage par chatSettings
 * 
 * ✅ CORRIGÉ: La sanitization ne fait plus d'encodage HTML (entités)
 *    Firestore stocke du texte brut, pas du HTML.
 *    On supprime les injections dangereuses SANS encoder les caractères normaux.
 * 
 * ✅ AJOUT: Soft delete — deleteMessage() marque un message comme supprimé
 */

import { create } from 'zustand';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  orderBy,
  where,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  limit,
  limitToLast,
  endBefore,
  getCountFromServer
} from 'firebase/firestore';
import { db, functions } from '@/config/firebase';
import { httpsCallable } from 'firebase/functions';

// ============================================================================
// SANITIZATION — SANS encodage HTML
// ============================================================================

const sanitizeMessageFallback = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  
  let sanitized = text
    // Supprimer les caractères de contrôle invisibles
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    // Supprimer les caractères zero-width
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    // Supprimer les balises <script>
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Supprimer javascript:
    .replace(/javascript:/gi, '')
    // Supprimer les attributs d'événements
    .replace(/on\w+\s*=/gi, '')
    // Supprimer toutes les balises HTML restantes
    .replace(/<[^>]*>/g, '');
  
  // Trim et normaliser les espaces
  sanitized = sanitized.trim().replace(/\s+/g, ' ');
  
  // Limiter la longueur
  if (sanitized.length > 250) {
    sanitized = sanitized.substring(0, 250);
  }
  
  return sanitized;
};

// ============================================================================
// HELPERS
// ============================================================================

const formatLastMessageTime = (ts: Timestamp): string => {
  const msgTime = ts.toDate();
  const diffHours = (Date.now() - msgTime.getTime()) / (1000 * 60 * 60);
  if (diffHours < 24) return msgTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diffHours < 48) return 'Hier';
  return msgTime.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const getUnreadCount = async (
  residenceId: string,
  groupId: string,
  userLastRead: Date | undefined
): Promise<number> => {
  const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupId, 'messages');
  const q = userLastRead
    ? query(messagesRef, where('createdAt', '>', Timestamp.fromDate(userLastRead)), where('deleted', '==', false))
    : query(messagesRef, where('deleted', '==', false));
  const snap = await getCountFromServer(q);
  return snap.data().count;
};

// ============================================================================
// TYPES
// ============================================================================

export interface ChatMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderPhotoURL?: string;
  timestamp: string;
  createdAt?: Timestamp;
  deleted?: boolean;
  deletedAt?: Timestamp;
  moderated?: boolean;
  moderatedAt?: Timestamp;
  moderatedBy?: string;
}

export interface ChatGroup {
  id: string;
  name: string;
  type: 'residence' | 'owners' | 'council';
  lastMessage?: string;
  lastMessageTime?: string;
  lastSenderName?: string;
  unreadCount: number;
}

export interface ChatSettings {
  residence?: boolean;
  owners?: boolean;
  council?: boolean;
}

interface ChatState {
  groups: ChatGroup[];
  messagesByGroup: Record<string, ChatMessage[]>;
  olderMessagesByGroup: Record<string, ChatMessage[]>;
  hasMoreByGroup: Record<string, boolean>;
  isLoadingGroups: boolean;
  isLoadingMessages: boolean;
  isLoadingMore: boolean;
  isSending: boolean;
  isDeleting: boolean;
  unsubscribers: Record<string, () => void>;
  groupListeners: (() => void)[];
  userLastReadAt: Record<string, Date | undefined>;

  loadGroups: (residenceId: string, userCategory: string, userId: string) => Promise<void>;
  subscribeToMessages: (residenceId: string, groupId: string) => void;
  unsubscribeFromMessages: (groupId: string) => void;
  loadOlderMessages: (residenceId: string, groupId: string) => Promise<void>;
  sendMessage: (residenceId: string, groupId: string, text: string, senderId: string, senderName: string, senderPhotoURL?: string) => Promise<void>;
  deleteMessage: (residenceId: string, groupId: string, messageId: string, userId: string) => Promise<void>;
  moderateMessage: (residenceId: string, groupId: string, messageId: string) => Promise<void>;
  markAsRead: (groupId: string, userId: string) => Promise<void>;
  getMessages: (groupId: string) => ChatMessage[];
  getTotalUnread: () => number;
  clearGroupListeners: () => void;
  clearGroups: () => void;
  resetStore: () => void;
}

// ============================================================================
// STORE
// ============================================================================

export const useChatStore = create<ChatState>((set, get) => ({
  groups: [],
  messagesByGroup: {},
  olderMessagesByGroup: {},
  hasMoreByGroup: {},
  isLoadingGroups: false,
  isLoadingMessages: false,
  isLoadingMore: false,
  isSending: false,
  isDeleting: false,
  unsubscribers: {},
  groupListeners: [],
  userLastReadAt: {},

  loadGroups: async (residenceId: string, userCategory: string, userId: string) => {
    set({ isLoadingGroups: true });

    // Nettoyer les anciens listeners
    get().clearGroupListeners();

    try {
      // Récupérer les chatSettings
      const residenceRef = doc(db, 'residences', residenceId);
      const residenceSnap = await getDoc(residenceRef);
      let chatSettings: ChatSettings = { residence: true, owners: true, council: true };
      if (residenceSnap.exists()) {
        chatSettings = residenceSnap.data().chatSettings || chatSettings;
      }

      // Permissions spécifiques au gardien (configurées par le CS)
      const isGardien = userCategory === 'gardien';
      const gardienSettings = residenceSnap.exists()
        ? (residenceSnap.data().gardienSettings || {})
        : {};

      // Filtrer les groupes selon les permissions
      const availableGroups: { id: string; name: string; type: 'residence' | 'owners' | 'council' }[] = [];
      if (chatSettings.residence === true) {
        // Résidence : accessible à tous sauf gardien sans permission explicite
        if (!isGardien || gardienSettings.chatResidence === true) {
          availableGroups.push({ id: 'residence', name: 'Résidence', type: 'residence' });
        }
      }
      if (chatSettings.owners === true) {
        if (['owner', 'council', 'syndic', 'admin'].includes(userCategory) ||
            (isGardien && gardienSettings.chatOwners === true)) {
          availableGroups.push({ id: 'owners', name: 'Copropriétaires', type: 'owners' });
        }
      }
      if (chatSettings.council === true) {
        if (['council', 'syndic', 'admin'].includes(userCategory) ||
            (isGardien && gardienSettings.chatCouncil === true)) {
          availableGroups.push({ id: 'council', name: 'Conseil Syndical', type: 'council' });
        }
      }

      // Charger lastReadAt de l'utilisateur (une seule requête)
      const memberRef = doc(db, 'members', userId);
      const memberSnap = await getDoc(memberRef);
      const lastReadAtMap: Record<string, Date | undefined> = {};
      if (memberSnap.exists()) {
        const memberData = memberSnap.data();
        for (const [gId, ts] of Object.entries(memberData.lastReadAt || {})) {
          lastReadAtMap[gId] = (ts as Timestamp).toDate();
        }
      }
      set({ userLastReadAt: lastReadAtMap });

      // Chargement initial + setup des listeners temps réel
      const groups: ChatGroup[] = [];
      const newGroupListeners: (() => void)[] = [];

      for (const groupDef of availableGroups) {
        const groupRef = doc(db, 'residences', residenceId, 'chatGroups', groupDef.id);
        const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupDef.id, 'messages');
        const groupSnap = await getDoc(groupRef);

        let lastMessage = 'Aucun message';
        let lastMessageTime = '';
        let lastSenderName = '';
        let unreadCount = 0;

        // Charger le dernier message visible (non supprimé, non modéré)
        const initialMsgSnap = await getDocs(query(messagesRef, orderBy('createdAt', 'desc'), limit(5)));
        const lastVisibleDoc = initialMsgSnap.docs.find(d => !d.data().deleted && !d.data().moderated);
        if (lastVisibleDoc) {
          lastSenderName = lastVisibleDoc.data().senderName || '';
          const msgText = lastVisibleDoc.data().text || '';
          lastMessage = (lastSenderName ? `${lastSenderName}: ${msgText}` : msgText).substring(0, 50);
          if (lastVisibleDoc.data().createdAt) {
            lastMessageTime = formatLastMessageTime(lastVisibleDoc.data().createdAt);
          }
        }

        if (groupSnap.exists()) {
          const data = groupSnap.data();
          const groupLastMsg = data.lastMessageTime?.toDate();
          const userLastRead = lastReadAtMap[groupDef.id];
          if (groupLastMsg && (!userLastRead || userLastRead < groupLastMsg)) {
            unreadCount = await getUnreadCount(residenceId, groupDef.id, userLastRead);
          }
        }

        groups.push({ id: groupDef.id, name: groupDef.name, type: groupDef.type, lastMessage, lastMessageTime, lastSenderName, unreadCount });

        // Listener sur les derniers messages — met à jour l'aperçu en temps réel
        // (gère la suppression et la modération sans dépendre du champ dénormalisé du groupe)
        const msgUnsubscribe = onSnapshot(
          query(messagesRef, orderBy('createdAt', 'desc'), limit(5)),
          (msgSnap) => {
            const lastDoc = msgSnap.docs.find(d => !d.data().deleted && !d.data().moderated);
            const senderName = lastDoc?.data().senderName || '';
            const msgText = lastDoc?.data().text || '';
            const lastMsg = msgText
              ? (senderName ? `${senderName}: ${msgText}` : msgText).substring(0, 50)
              : 'Aucun message';
            const lastMsgTime = lastDoc?.data().createdAt
              ? formatLastMessageTime(lastDoc.data().createdAt)
              : '';
            set(state => ({
              groups: state.groups.map(g =>
                g.id === groupDef.id
                  ? { ...g, lastMessage: lastMsg, lastMessageTime: lastMsgTime, lastSenderName: senderName }
                  : g
              )
            }));
          },
          (error) => {
            if ((error as { code?: string }).code !== 'permission-denied') {
              console.error('[chatStore] Messages listener error:', error);
            }
          }
        );

        // Listener sur le doc groupe — uniquement pour le compteur non-lus
        const groupUnsubscribe = onSnapshot(groupRef, async (snap) => {
          if (!snap.exists()) return;
          const data = snap.data();
          const groupLastMsg = data.lastMessageTime?.toDate();
          const currentLastReadAt = get().userLastReadAt;
          const userLastRead = currentLastReadAt[groupDef.id];

          let newUnreadCount = 0;
          if (groupLastMsg && (!userLastRead || userLastRead < groupLastMsg)) {
            newUnreadCount = await getUnreadCount(residenceId, groupDef.id, userLastRead);
          }

          set(state => ({
            groups: state.groups.map(g =>
              g.id === groupDef.id ? { ...g, unreadCount: newUnreadCount } : g
            )
          }));
        }, (error) => {
          if ((error as { code?: string }).code !== 'permission-denied') {
            console.error('[chatStore] Group listener error:', error);
          }
        });

        newGroupListeners.push(msgUnsubscribe, groupUnsubscribe);
      }

      set({ groups, isLoadingGroups: false, groupListeners: newGroupListeners });
    } catch (error) {
      console.error('Error loading chat groups:', error);
      set({ groups: [], isLoadingGroups: false });
    }
  },

  clearGroupListeners: () => {
    get().groupListeners.forEach(u => u());
    set({ groupListeners: [] });
  },

  clearGroups: () => {
    get().clearGroupListeners();
    set({ groups: [], userLastReadAt: {} });
  },

  resetStore: () => {
    get().groupListeners.forEach(u => u());
    Object.values(get().unsubscribers).forEach(u => u());
    set({
      groups: [],
      groupListeners: [],
      unsubscribers: {},
      messagesByGroup: {},
      olderMessagesByGroup: {},
      hasMoreByGroup: {},
      userLastReadAt: {},
    });
  },

  subscribeToMessages: (residenceId: string, groupId: string) => {
    if (get().unsubscribers[groupId]) {
      return;
    }

    set({ isLoadingMessages: true });

    try {
      const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupId, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'asc'), limitToLast(100));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messages: ChatMessage[] = [];
        
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          messages.push({
            id: docSnap.id,
            text: data.text || '',
            senderId: data.senderId || '',
            senderName: data.senderName || 'Anonyme',
            senderPhotoURL: data.senderPhotoURL || undefined,
            timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            createdAt: data.createdAt,
            deleted: data.deleted || false,
            deletedAt: data.deletedAt || null,
            moderated: data.moderated || false,
            moderatedBy: data.moderatedBy || undefined
          });
        });

        set(state => ({
          messagesByGroup: {
            ...state.messagesByGroup,
            [groupId]: messages
          },
          // Si la fenêtre live contient moins de 100 messages, elle contient
          // forcément tout l'historique : pas de messages plus anciens à charger
          hasMoreByGroup: {
            ...state.hasMoreByGroup,
            [groupId]: messages.length >= 100
          },
          isLoadingMessages: false
        }));
      }, (error) => {
        if ((error as { code?: string }).code !== 'permission-denied') {
          console.error('Error listening to messages:', error);
          set({ isLoadingMessages: false });
        }
      });

      set(state => ({
        unsubscribers: {
          ...state.unsubscribers,
          [groupId]: unsubscribe
        }
      }));
    } catch (error) {
      console.error('Error subscribing to messages:', error);
      set({ isLoadingMessages: false });
    }
  },

  unsubscribeFromMessages: (groupId: string) => {
    const unsubscribe = get().unsubscribers[groupId];
    if (unsubscribe) {
      unsubscribe();
      set(state => {
        const { [groupId]: _, ...rest } = state.unsubscribers;
        const { [groupId]: _older, ...restOlder } = state.olderMessagesByGroup;
        const { [groupId]: _hasMore, ...restHasMore } = state.hasMoreByGroup;
        return { unsubscribers: rest, olderMessagesByGroup: restOlder, hasMoreByGroup: restHasMore };
      });
    }
  },

  loadOlderMessages: async (residenceId: string, groupId: string) => {
    const state = get();
    if (state.isLoadingMore) return;
    if (state.hasMoreByGroup[groupId] === false) return;

    const oldest = get().getMessages(groupId)[0];
    if (!oldest?.createdAt) return;

    set({ isLoadingMore: true });

    try {
      const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupId, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'asc'), endBefore(oldest.createdAt), limitToLast(50));
      const snap = await getDocs(q);

      const olderMessages: ChatMessage[] = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        olderMessages.push({
          id: docSnap.id,
          text: data.text || '',
          senderId: data.senderId || '',
          senderName: data.senderName || 'Anonyme',
          senderPhotoURL: data.senderPhotoURL || undefined,
          timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          createdAt: data.createdAt,
          deleted: data.deleted || false,
          deletedAt: data.deletedAt || null
        });
      });

      set(state => ({
        olderMessagesByGroup: {
          ...state.olderMessagesByGroup,
          [groupId]: [...olderMessages, ...(state.olderMessagesByGroup[groupId] || [])]
        },
        hasMoreByGroup: {
          ...state.hasMoreByGroup,
          [groupId]: olderMessages.length >= 50
        },
        isLoadingMore: false
      }));
    } catch (error) {
      console.error('Error loading older messages:', error);
      set({ isLoadingMore: false });
    }
  },

  // ✅ ENVOI SÉCURISÉ
  sendMessage: async (residenceId: string, groupId: string, text: string, senderId: string, senderName: string, senderPhotoURL?: string) => {
    if (!text.trim()) return;

    set({ isSending: true });

    try {
      // Sanitizer le message (supprime injections, garde les caractères normaux)
      const sanitizedText = sanitizeMessageFallback(text);
      
      if (!sanitizedText) {
        throw new Error('Message invalide');
      }

      // Ajouter le message
      const messagesRef = collection(db, 'residences', residenceId, 'chatGroups', groupId, 'messages');
      await addDoc(messagesRef, {
        text: sanitizedText,
        senderId,
        senderName: sanitizeMessageFallback(senderName),
        ...(senderPhotoURL ? { senderPhotoURL } : {}),
        createdAt: serverTimestamp(),
        deleted: false
      });

      // Mettre à jour le groupe
      const groupRef = doc(db, 'residences', residenceId, 'chatGroups', groupId);
      await setDoc(groupRef, {
        lastMessage: sanitizedText.substring(0, 100),
        lastSenderName: senderName,
        lastSenderId: senderId,
        lastMessageTime: serverTimestamp()
      }, { merge: true });

    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    } finally {
      set({ isSending: false });
    }
  },

  // ✅ SOFT DELETE — marque le message comme supprimé
  deleteMessage: async (residenceId: string, groupId: string, messageId: string, userId: string) => {
    set({ isDeleting: true });

    try {
      // Vérifier que le message appartient bien à l'utilisateur
      const messageRef = doc(db, 'residences', residenceId, 'chatGroups', groupId, 'messages', messageId);
      const messageSnap = await getDoc(messageRef);

      if (!messageSnap.exists()) {
        throw new Error('Message introuvable');
      }

      const messageData = messageSnap.data();
      if (messageData.senderId !== userId) {
        throw new Error('Vous ne pouvez supprimer que vos propres messages');
      }

      if (messageData.deleted) {
        throw new Error('Message déjà supprimé');
      }

      // Soft delete : on garde le document, on efface le texte et on flag
      await updateDoc(messageRef, {
        deleted: true,
        deletedAt: serverTimestamp(),
        text: '' // On vide le texte par sécurité
      });

      // Note : pas besoin de mettre à jour le state local,
      // onSnapshot le fera automatiquement via le listener temps réel

    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    } finally {
      set({ isDeleting: false });
    }
  },

  moderateMessage: async (residenceId: string, groupId: string, messageId: string) => {
    set({ isDeleting: true });
    try {
      const fn = httpsCallable(functions, 'moderateChatMessage');
      await fn({ residenceId, groupId, messageId });
    } catch (error: unknown) {
      console.error('Error moderating message:', error);
      throw error;
    } finally {
      set({ isDeleting: false });
    }
  },

  markAsRead: async (groupId: string, userId: string) => {
    try {
      const memberRef = doc(db, 'members', userId);
      await updateDoc(memberRef, {
        [`lastReadAt.${groupId}`]: serverTimestamp()
      });

      const now = new Date();
      set(state => ({
        userLastReadAt: { ...state.userLastReadAt, [groupId]: now },
        groups: state.groups.map(g =>
          g.id === groupId ? { ...g, unreadCount: 0 } : g
        )
      }));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  },

  getMessages: (groupId: string) => {
    const older = get().olderMessagesByGroup[groupId] || [];
    const recent = get().messagesByGroup[groupId] || [];
    return [...older, ...recent];
  },

  getTotalUnread: () => {
    const groups = get().groups;
    if (!Array.isArray(groups)) return 0;
    return groups.reduce((sum, g) => sum + (g.unreadCount || 0), 0);
  }
}));

export default useChatStore;
